import { ChartContainer, ChartTooltip } from "@/components/chart";
import ChartTemplate from "@/components/chart-template";
import { H3 } from "@/components/typography";
import { MetricRow } from "@/http/api-types";
import React, { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, XAxis, YAxis } from "recharts";
import { BusinessSection } from "./business-tables";
import { formatValue } from "./columns";

interface BusinessMetricsChartsProps {
  months: string[];
  sections: BusinessSection[];
}

interface ChartDataPoint {
  month: string;
  value: number | undefined;
  lowThreshold: number | undefined;
  highThreshold: number | undefined;
}

function buildChartData(months: string[], row: MetricRow): ChartDataPoint[] {
  // Reverse months so oldest is first (left-to-right chronological order)
  const chronological = [...months].reverse();
  return chronological.map(month => ({
    month,
    value: row.values[month],
    lowThreshold: row.low_thresholds?.[month] ?? row.low_threshold ?? undefined,
    highThreshold: row.high_thresholds?.[month] ?? row.high_threshold ?? undefined,
  }));
}

function compactFormatValue(value: number, format: string): string {
  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-IE", {
        style: "currency",
        currency: "EUR",
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(value);
    case "percent":
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case "integer":
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 0,
      }).format(value);
    case "float":
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value);
    default:
      return value.toString();
  }
}

const CustomTooltip = ({ active, payload, format }: { active?: boolean; payload?: any[]; format: string }) => {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0]?.payload as ChartDataPoint | undefined;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
      <div className="font-medium mb-1">{point.month}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "hsl(var(--chart-1))" }} />
        <span className="font-mono font-medium tabular-nums">{formatValue(point.value, format)}</span>
      </div>
      {point.lowThreshold !== undefined && (
        <div className="text-muted-foreground mt-0.5">Low: {formatValue(point.lowThreshold, format)}</div>
      )}
      {point.highThreshold !== undefined && (
        <div className="text-muted-foreground mt-0.5">High: {formatValue(point.highThreshold, format)}</div>
      )}
    </div>
  );
};

const BusinessMetricChart: React.FC<{
  row: MetricRow;
  months: string[];
}> = ({ row, months }) => {
  const data = useMemo(() => buildChartData(months, row), [months, row]);

  const { yMin, yMax, hasLowThreshold, hasHighThreshold, hasDynamicThresholds } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    let hasLow = false;
    let hasHigh = false;
    let isDynamic = false;

    // Check if thresholds vary across months
    const lowVals = new Set<number>();
    const highVals = new Set<number>();

    for (const d of data) {
      if (d.value !== undefined) {
        min = Math.min(min, d.value);
        max = Math.max(max, d.value);
      }
      if (d.lowThreshold !== undefined) {
        hasLow = true;
        lowVals.add(d.lowThreshold);
        min = Math.min(min, d.lowThreshold);
        max = Math.max(max, d.lowThreshold);
      }
      if (d.highThreshold !== undefined) {
        hasHigh = true;
        highVals.add(d.highThreshold);
        min = Math.min(min, d.highThreshold);
        max = Math.max(max, d.highThreshold);
      }
    }

    if (lowVals.size > 1 || highVals.size > 1) {
      isDynamic = true;
    }

    if (min === Infinity) min = 0;
    if (max === -Infinity) max = 1;

    // Add some padding
    const range = max - min || 1;
    const paddedMin = Math.min(0, min - range * 0.05);
    const paddedMax = max + range * 0.1;

    return {
      yMin: paddedMin,
      yMax: paddedMax,
      hasLowThreshold: hasLow,
      hasHighThreshold: hasHigh,
      hasDynamicThresholds: isDynamic,
    };
  }, [data]);

  // For static thresholds, grab the first defined value
  const staticLow = data.find(d => d.lowThreshold !== undefined)?.lowThreshold;
  const staticHigh = data.find(d => d.highThreshold !== undefined)?.highThreshold;

  const chartContent = (
    <ChartContainer config={{}}>
      <LineChart accessibilityLayer data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value: string) => {
            // Show abbreviated month: "2024-01" -> "Jan 24"
            const [year, month] = value.split("-");
            const date = new Date(Number(year), Number(month) - 1);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "2-digit",
            });
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value: number) => compactFormatValue(value, row.format)}
          domain={[yMin, yMax]}
        />
        <ChartTooltip cursor={false} content={<CustomTooltip format={row.format} />} />

        {/* Shaded threshold regions */}
        {!hasDynamicThresholds && hasLowThreshold && staticLow !== undefined && (
          <ReferenceArea
            y1={staticLow}
            y2={hasHighThreshold && staticHigh !== undefined ? staticHigh : yMax}
            fill="#22c55e"
            fillOpacity={0.08}
            ifOverflow="hidden"
          />
        )}
        {!hasDynamicThresholds && hasHighThreshold && staticHigh !== undefined && (
          <ReferenceArea y1={staticHigh} y2={yMax} fill="#ef4444" fillOpacity={0.08} ifOverflow="hidden" />
        )}

        {/* For static thresholds, also add a subtle reference line at the boundary */}
        {!hasDynamicThresholds && hasLowThreshold && staticLow !== undefined && (
          <ReferenceLine
            y={staticLow}
            stroke="#22c55e"
            strokeDasharray="4 3"
            strokeWidth={1}
            label={{
              value: "Low",
              position: "insideTopLeft",
              className: "text-[10px] fill-green-600",
            }}
          />
        )}
        {!hasDynamicThresholds && hasHighThreshold && staticHigh !== undefined && (
          <ReferenceLine
            y={staticHigh}
            stroke="#ef4444"
            strokeDasharray="4 3"
            strokeWidth={1}
            label={{
              value: "High",
              position: "insideTopLeft",
              className: "text-[10px] fill-red-600",
            }}
          />
        )}

        {/* For dynamic (per-month) thresholds, render as dashed line series */}
        {hasDynamicThresholds && hasLowThreshold && (
          <Line
            dataKey="lowThreshold"
            type="stepAfter"
            stroke="#22c55e"
            strokeWidth={1}
            strokeDasharray="4 3"
            dot={false}
            name="Low Threshold"
            connectNulls
          />
        )}
        {hasDynamicThresholds && hasHighThreshold && (
          <Line
            dataKey="highThreshold"
            type="stepAfter"
            stroke="#ef4444"
            strokeWidth={1}
            strokeDasharray="4 3"
            dot={false}
            name="High Threshold"
            connectNulls
          />
        )}

        {/* Main metric line */}
        <Line dataKey="value" type="linear" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} connectNulls />
      </LineChart>
    </ChartContainer>
  );

  return <ChartTemplate title={row.metric} chart={chartContent} table={null} timeSplittable={false} />;
};

export const BusinessMetricsCharts: React.FC<BusinessMetricsChartsProps> = ({ months, sections }) => {
  return (
    <div className="space-y-8">
      {sections.map(section => (
        <div key={section.title}>
          <H3>{section.title}</H3>
          <div className="grid grid-cols-12 gap-4">
            {section.rows.map(row => (
              <div key={row.metric} className="col-span-6">
                <BusinessMetricChart row={row} months={months} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
